import { createGetApiHook, createPostApiHook } from "../../api/apiHooks";
import { APIResponseType } from "@/types/generics/APIResponse.type";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { StageModeAPI } from "../talent/talent_api";

const API = axios.create({
  baseURL: "/api",
});

API.interceptors.request.use((config) => {
  const token = localStorage.getItem("access_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// 1. Load all interactions for a session
export const useModeratorLoadInteractions = (sessionId: string) => {
  return useQuery({
    queryKey: ["interactions", sessionId],
    queryFn: async () => {
      const res = await API.get(`/interactions?sessionId=${sessionId}`);
      return res.data;
    },
    enabled: !!sessionId,
    staleTime: 10000,
    refetchInterval: 15000, // auto-poll every 15s
    retry: 1,
  });
};

// 2. Approve or hide a Q&A
export const useModeratorApproveQnA = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      status,
    }: {
      id: string;
      status: "approved" | "hidden";
    }) => API.patch(`/qna/${id}`, { status }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["interactions"] });
    },
    onError: (error) => {
      console.error("Failed to update Q&A status:", error);
    },
  });
};

// 3. Trigger overlay
export const useModeratorTriggerOverlay = () => {
  return useMutation({
    mutationFn: (payload: {
      sessionId: string;
      overlayType: string;
      data?: Record<string, any>;
    }) => API.post("/overlays", payload),
    onError: (error) => {
      console.error("Failed to trigger overlay:", error);
    },
  });
};

// 4. Create poll
export const useModeratorCreatePoll = () => {
  return useMutation({
    mutationFn: (payload: {
      sessionId: string;
      question: string;
      options: string[];
    }) => API.post("/polls", payload),
    onError: (error) => {
      console.error("Failed to create poll:", error);
    },
  });
};

// 5. Close or reopen poll
export const useModeratorCloseOrOpenPoll = () => {
  return useMutation({
    mutationFn: (payload: { id: string; status: "closed" | "active" }) =>
      API.patch(`/polls/${payload.id}`, { status: payload.status }),
    onError: (error) => {
      console.error("Failed to update poll status:", error);
    },
  });
};

const getGuestUsers = ({ id }: { id: string }) =>
  `stagecards/booking/${id}/users`;
const acceptPerformerInvitationEndpoint = ({
  id,
  userId,
}: {
  id: string | number;
  userId: string | number;
}) => `stagecards/booking/${id}/user/${userId}/invite-performer-accept`;
const rejectPerformerInvitationEndpoint = ({
  id,
  userId,
}: {
  id: string | number;
  userId: string | number;
}) => `stagecards/booking/${id}/user/${userId}/invite-performer-reject`;
const checkInviteStatusEndpoint = ({
  id,
  userId,
}: {
  id: string | number;
  userId: string | number;
}) => `stagecards/booking/${id}/user/${userId}/invite-performer-status`;
const getYoutubeRequests = ({ id }: { id: string }) =>
  `stagecards/booking/${id}/youtube-url`;

export const useGetGuestUsers = createGetApiHook<
  APIResponseType<any[]>,
  { id: string }
>(getGuestUsers);

export const useInviteGuest = (bookingId: string | number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId }: { userId: string | number }) =>
      StageModeAPI.post(
        `/stagecards/booking/${bookingId}/user/${userId}/invite-performer`,
        {}
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["guest-users", bookingId],
      });
    },
    onError: (error) => {
      console.error("Failed to respond to booking:", error);
    },
  });
};

export const useAcceptPerformerInvitation = createPostApiHook<
  {},
  { id: string | number; userId: string | number },
  APIResponseType<any>
>(acceptPerformerInvitationEndpoint, {
  isFormDataMethod: false,
});

export const useRejectPerformerInvitation = createPostApiHook<
  {},
  { id: string | number; userId: string | number },
  APIResponseType<any>
>(rejectPerformerInvitationEndpoint, {
  isFormDataMethod: false,
});

export const useCheckInviteStatus = createPostApiHook<
  {},
  { id: string | number; userId: string | number },
  APIResponseType<any>
>(checkInviteStatusEndpoint, {
  isFormDataMethod: false,
});

export const useGetYoutubeRequests = createGetApiHook<
  APIResponseType<any[]>,
  { id: string }
>(getYoutubeRequests);

export const useAssignModerator = (bookingId: string | number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId }: { userId: string | number }) =>
      StageModeAPI.post(
        `/stagecards/booking/${bookingId}/user/${userId}/invite-moderator`,
        {}
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["guest-users", bookingId] });
    },
    onError: (error) => {
      console.error("Failed to assign moderator:", error);
    },
  });
};

export const useRevokeModerator = (bookingId: string | number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId }: { userId: string | number }) =>
      StageModeAPI.patch(
        `/stagecards/booking/${bookingId}/moderators/${userId}/revoke`,
        {}
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["guest-users", bookingId] });
    },
    onError: (error) => {
      console.error("Failed to revoke moderator:", error);
    },
  });
};


export const useAcceptModeratorInvitation = createPostApiHook<
  {},
  { id: string | number; userId: string | number },
  APIResponseType<any>
>(({ id, userId }) => `stagecards/booking/${id}/user/${userId}/invite-moderator-accept`, {
  isFormDataMethod: false,
});

export const useRejectModeratorInvitation = createPostApiHook<
  {},
  { id: string | number; userId: string | number },
  APIResponseType<any>
>(({ id, userId }) => `stagecards/booking/${id}/user/${userId}/invite-moderator-reject`, {
  isFormDataMethod: false,
});

export const useCheckModeratorInviteStatus = createPostApiHook<
  {},
  { id: string | number; userId: string | number },
  APIResponseType<{ status: "Pending" | "Accepted" | "Rejected" }>
>(({ id, userId }) => `stagecards/booking/${id}/user/${userId}/invite-moderator-status`, {
  isFormDataMethod: false,
});
